import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Shield, Mail, Calendar, UserCheck, UserX, Edit, KeyRound } from "lucide-react";
import { api } from "@/api/client";
import { useAuthStore } from "@/store/authSlice";

interface UserDetail {
  id: string;
  username: string;
  email: string;
  full_name: string;
  nip?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  roles: Array<{ id: string; name: string; description?: string }>;
}

const ROLE_COLORS: Record<string, string> = {
  ADMIN: "bg-red-100 text-red-700",
  LEADER: "bg-purple-100 text-purple-700",
  AUDITOR: "bg-yellow-100 text-yellow-700",
  MANAGER: "bg-blue-100 text-blue-700",
  TREASURER: "bg-green-100 text-green-700",
  USER: "bg-gray-100 text-gray-700",
  GUEST: "bg-slate-100 text-slate-600",
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.user_id);
  const isSelf = id === currentUserId;

  const { data: user, isLoading } = useQuery<UserDetail>({
    queryKey: ["user", id],
    queryFn: () => api.get(`/users/${id}`),
    enabled: !!id,
  });

  const toggleMutation = useMutation({
    mutationFn: (isActive: boolean) => api.put(`/users/${id}`, { is_active: isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user", id] }),
  });

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/users" className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detail Pengguna</h1>
            <p className="text-sm text-gray-500 mt-0.5">@{user.username}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/users/${id}/edit`}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
          {!isSelf && (
            <button
              onClick={() => toggleMutation.mutate(!user.is_active)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${
                user.is_active
                  ? "border border-red-200 text-red-600 hover:bg-red-50"
                  : "border border-green-200 text-green-600 hover:bg-green-50"
              }`}
            >
              {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
              {user.is_active ? "Nonaktifkan" : "Aktifkan"}
            </button>
          )}
        </div>
      </div>

      {/* Profile Card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xl">
              {user.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{user.full_name}</h2>
              <p className="text-sm text-gray-500">@{user.username}</p>
              <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}>
                {user.is_active ? <><UserCheck className="h-3 w-3" /> Aktif</> : <><UserX className="h-3 w-3" /> Nonaktif</>}
              </span>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-6 px-6 py-5 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Email</p>
            <p className="text-gray-800 flex items-center gap-1.5">
              <Mail className="h-4 w-4 text-gray-400" />
              {user.email}
            </p>
          </div>
          {user.nip && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">NIP</p>
              <p className="text-gray-800 flex items-center gap-1.5">
                <KeyRound className="h-4 w-4 text-gray-400" />
                {user.nip}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Terdaftar</p>
            <p className="text-gray-800 flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              {new Date(user.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Diperbarui</p>
            <p className="text-gray-800 flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              {new Date(user.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>

        {/* Roles */}
        <div className="border-t border-gray-100 px-6 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Role</p>
          <div className="flex flex-wrap gap-2">
            {user.roles.map((role) => (
              <span
                key={role.id}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${ROLE_COLORS[role.name] ?? "bg-gray-100 text-gray-700"}`}
              >
                <Shield className="h-3.5 w-3.5" />
                {role.name}
              </span>
            ))}
            {user.roles.length === 0 && (
              <span className="text-sm text-gray-400">Tidak ada role ditetapkan</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
